// TimelineQuarterView.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
    buildTopHeaderSegments,
    DAY_MS,
    formatTimelineRangeText,
    getInclusiveEndOffsetMs,
    getScheduledBounds,
    getScheduledRows,
    getTimelineBarStyle,
    getUnscheduledLabel,
    resolveViewportTimelineBounds,
} from '../shared/timelineViewUtils';

// CSS
import './TimelineQuarterView.css';

const QUARTER_COLUMN_WIDTH = 120;
const MIN_TRACK_WIDTH = 720;
const MIN_VISIBLE_QUARTERS = Math.ceil(MIN_TRACK_WIDTH / QUARTER_COLUMN_WIDTH);

const startOfDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const endOfDay = (date) => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next;
};

const addMonths = (date, months) => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + months);
    return next;
};

const addQuarters = (date, quarters) => addMonths(date, quarters * 3);

const startOfQuarter = (date) => {
    const next = startOfDay(date);
    next.setDate(1);
    next.setMonth(Math.floor(next.getMonth() / 3) * 3);
    return next;
};

/**
 * TimelineQuarterView
 *
 * Right-side timeline renderer for quarter-scale timeline.
 *
 * Top row: Years
 * Bottom row: Quarters (Q1..Q4)
 *
 * @returns {ReactNode} The rendered component.
 */
const TimelineQuarterView = React.memo(({
    timelineRows = [],
    timelineFieldDef,
    viewportBounds = null,
    onNodeClick,
    onNodeDoubleClick,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    // ----- STATE ----- //

    const [loadedTrailingQuarters, setLoadedTrailingQuarters] = useState(0);
    // ----- REFS ----- //

    const scrollRef = useRef(null);
    const lastLoadTriggerWidthRef = useRef(0);

    // ----- MEMOS ----- //

    const scheduledRows = useMemo(() => getScheduledRows(timelineRows), [timelineRows]);

    const scheduledSpanResetToken = useMemo(() => {
        const scheduledBounds = getScheduledBounds(scheduledRows);
        if (!scheduledBounds) return `none_${timelineFieldDef?.key || timelineFieldDef?.name || ''}`;
        return `${scheduledBounds.minStart.getTime()}_${scheduledBounds.maxEnd.getTime()}_${timelineFieldDef?.key || timelineFieldDef?.name || ''}`;
    }, [scheduledRows, timelineFieldDef?.key, timelineFieldDef?.name]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setLoadedTrailingQuarters(0);
        lastLoadTriggerWidthRef.current = 0;
    }, [scheduledSpanResetToken]);

    // ----- MEMOS ----- //

    const bounds = useMemo(() => {
        const viewport = resolveViewportTimelineBounds(viewportBounds);
        if (viewport) return viewport;

        const scheduledBounds = getScheduledBounds(scheduledRows);
        if (!scheduledBounds) return null;

        const startQuarter = startOfQuarter(scheduledBounds.minStart);
        const dataEndQuarter = startOfQuarter(scheduledBounds.maxEnd);
        const minSpanEndQuarter = startOfQuarter(addQuarters(startQuarter, MIN_VISIBLE_QUARTERS - 1));
        const baseEndQuarter = dataEndQuarter.getTime() < minSpanEndQuarter.getTime()
            ? minSpanEndQuarter
            : dataEndQuarter;
        const endQuarter = loadedTrailingQuarters > 0
            ? startOfQuarter(addQuarters(baseEndQuarter, loadedTrailingQuarters))
            : baseEndQuarter;

        const start = startQuarter;
        const end = endOfDay(addQuarters(endQuarter, 1));
        return { start, end };
    }, [scheduledRows, loadedTrailingQuarters, viewportBounds]);

    // ----- HANDLERS ----- //

    const handleScroll = useCallback(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const nearRightEdge = scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 48;
        if (!nearRightEdge) return;

        if (lastLoadTriggerWidthRef.current === scrollEl.scrollWidth) return;
        lastLoadTriggerWidthRef.current = scrollEl.scrollWidth;
        setLoadedTrailingQuarters((prev) => prev + 1);
    }, []);

    // ----- EFFECTS ----- //

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const onScroll = () => handleScroll();
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        return () => scrollEl.removeEventListener('scroll', onScroll);
    }, [handleScroll]);

    // ----- MEMOS ----- //

    const columns = useMemo(() => {
        if (!bounds) return [];

        const out = [];
        let cursor = startOfQuarter(bounds.start);

        while (cursor.getTime() <= bounds.end.getTime()) {
            const quarterStart = new Date(cursor);
            const next = startOfQuarter(addQuarters(cursor, 1));
            const quarterEnd = new Date(next.getTime() - 1);
            out.push({
                key: `q_${quarterStart.getTime()}`,
                start: quarterStart,
                end: quarterEnd,
                topLabel: quarterStart.toLocaleDateString('en-US', { year: 'numeric' }),
                bottomLabel: `Q${Math.floor(quarterStart.getMonth() / 3) + 1}`,
            });
            cursor = next;
        }

        return out;
    }, [bounds]);

    const topHeaderSegments = useMemo(
        () => buildTopHeaderSegments(columns, { labelKey: 'topLabel', keyPrefix: 'y' }),
        [columns]
    );

    const trackWidth = useMemo(() => (
        Math.max(MIN_TRACK_WIDTH, (columns.length || 1) * QUARTER_COLUMN_WIDTH)
    ), [columns.length]);

    const spanMs = useMemo(() => {
        if (!bounds) return 0;
        return Math.max(1, bounds.end.getTime() - bounds.start.getTime());
    }, [bounds]);

    if (!bounds || columns.length === 0) {

        return (
            <div className="timeline-quarter-view-container">

                {/* EMPTY */}
                <div
                  className="timeline-quarter-view-empty"
                >
                    No scheduled timeline items yet.
                </div>
            </div>
        );
    }
    // ----- RENDER ----- //

    return (
        <div
          className="timeline-quarter-view-container"
          style={{
                '--timeline-quarter-column-width': `${QUARTER_COLUMN_WIDTH}px`,
            }}
        >

            {/* SCROLL */}
            <div className="timeline-quarter-view-scroll" ref={scrollRef}>

                {/* TIMELINE QUARTER HEADER */}
                <div
                  className="timeline-quarter-header"
                  style={{
                        borderBottom: `1px solid ${theme.status}`,
                        width: `${trackWidth}px`,
                    }}
                >

                    {/* TIMELINE QUARTER HEADER ROW TOP */}
                    <div
                      className="timeline-quarter-header-row timeline-quarter-header-row-top"
                      style={{
                        borderBottom: `1px solid ${theme.status}`,
                      }}
                    >
                        {topHeaderSegments.map((segment) => (
                            <div
                              className="timeline-quarter-header-cell timeline-quarter-header-cell-top"
                              style={{
                                width: `${segment.span * QUARTER_COLUMN_WIDTH}px`,
                              }}
                              key={segment.key}
                              title={segment.label}
                            >
                                {segment.label}
                            </div>
                        ))}
                    </div>

                    {/* TIMELINE QUARTER HEADER ROW BOTTOM */}
                    <div
                      className="timeline-quarter-header-row timeline-quarter-header-row-bottom"
                      style={{
                            backgroundImage: `repeating-linear-gradient(to right, ${theme.status} 0, ${theme.status} 1px, transparent 1px, transparent ${QUARTER_COLUMN_WIDTH}px)`,
                        }}
                    >
                        {columns.map((column) => (
                            <div
                              className="timeline-quarter-header-cell timeline-quarter-header-cell-bottom"
                              style={{
                                width: `${QUARTER_COLUMN_WIDTH}px`,
                              }}
                              key={column.key}
                              title={column.bottomLabel}
                            >
                                {column.bottomLabel}
                            </div>
                        ))}
                    </div>
                </div>

                {/* TIMELINE QUARTER BODY */}
                <div
                  className="timeline-quarter-body"
                  style={{
                    width: `${trackWidth}px`,
                  }}
                >
                    {timelineRows.map((row, rowIndex) => {
                        const rowDividerStyle = rowIndex > 0
                            ? { borderTop: `1px solid ${theme.status}` }
                            : undefined;

                        if (row.type === 'group-header') {
                            return (
                                <div
                                  className="timeline-quarter-row timeline-quarter-row-group"
                                  style={{
                                    ...rowDividerStyle,
                                  }}
                                  key={row.key}
                                />
                            );
                        }

                        const entry = row.entry;
                        const isUnscheduled = row.type === 'item-unscheduled';
                        const barStyle = getTimelineBarStyle({
                            entry,
                            bounds,
                            spanMs,
                            trackWidth,
                            minDurationMs: DAY_MS,
                            minWidthPercent: 1.1,
                            inclusiveEndMs: getInclusiveEndOffsetMs(timelineFieldDef?.type),
                        });
                        const rangeText = formatTimelineRangeText(entry?.range, timelineFieldDef?.type);
                        const showBarLabel = barStyle?.widthPx >= 120;

                        return (
                            <div
                              className="timeline-quarter-row timeline-quarter-row-item"
                              style={{
                                    ...rowDividerStyle,
                                    backgroundImage: `repeating-linear-gradient(to right, ${theme.status} 0, ${theme.status} 1px, transparent 1px, transparent ${QUARTER_COLUMN_WIDTH}px)`,
                                }}
                              key={row.key}
                              onClick={(event) => onNodeClick?.(event, entry?.node)}
                              onDoubleClick={(event) => onNodeDoubleClick?.(event, entry?.node)}
                            >

                                {/* TIMELINE QUARTER UNSCHEDULED */}
                                {isUnscheduled && (
                                    <div
                                      className="timeline-quarter-unscheduled"
                                    >
                                        {getUnscheduledLabel(timelineFieldDef)}
                                    </div>
                                )}

                                {!isUnscheduled && barStyle && (
                                    <div
                                      className={`timeline-quarter-bar${entry?.range?.isMilestone ? ' is-milestone' : ''}`}
                                      style={{
                                            backgroundColor: entry?.barColor || theme.highlight,
                                            color: entry?.barForeground || undefined,
                                            left: barStyle.left,
                                            width: barStyle.width,
                                        }}
                                      title={rangeText}
                                    >

                                        {/* TIMELINE QUARTER BAR LABEL */}
                                        {showBarLabel && (
                                            <span
                                              className="timeline-quarter-bar-label"
                                            >{rangeText}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

// Dev Tools
TimelineQuarterView.displayName = 'TimelineQuarterView';

export default TimelineQuarterView;
